import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { 
  Users, 
  Mail, 
  TrendingUp, 
  Download, 
  Eye,
  EyeOff,
  BarChart3,
  Building2,
  Briefcase,
  Clock,
  Target,
  Zap,
  Filter,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  TrendingDown,
  Activity,
  UserCheck,
  MousePointer
} from 'lucide-react'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts'
import { 
  supabase, 
  DatabaseUser, 
  getJourneyAnalytics, 
  getPrimaryGoalsAnalysis 
} from '@/lib/supabase'

interface EnhancedAdminStats {
  // Basic metrics
  totalUsers: number
  emailConsentUsers: number
  businessTypes: Record<string, number>
  industries: Record<string, number>
  experienceLevels: Record<string, number>
  primaryGoals: Record<string, number>
  
  // Journey analytics
  journeyFunnel: Array<{ stage: string, users: number, completionRate: number }>
  avgTimePerStage: Record<number, number>
  dropOffPoints: Array<{ stage: number, dropOffRate: number }>
  
  // Segmentation data
  userSegments: Array<{
    segment: string
    count: number
    emailConsentRate: number
    averageEngagement: number
  }>
  
  // Time-based analytics
  dailySignups: Array<{ date: string, signups: number }>
  growthTrends: {
    dailyGrowthRate: number
    weeklyGrowthRate: number
    monthlyGrowthRate: number
  }
  
  // Engagement metrics
  engagementScores: Array<{ 
    userId: string
    score: number
    stagesCompleted: number
    timeSpent: number
  }>
  
  recentUsers: DatabaseUser[]
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C']

export function EnhancedAdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [stats, setStats] = useState<EnhancedAdminStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [dateRange, setDateRange] = useState('30d')

  const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123'

  useEffect(() => {
    // Check if already authenticated in this session
    const isAuth = sessionStorage.getItem('admin-authenticated')
    if (isAuth === 'true') {
      setIsAuthenticated(true)
      loadEnhancedStats()
    }
  }, [])

  const handleLogin = () => {
    if (password === adminPassword) {
      setIsAuthenticated(true)
      sessionStorage.setItem('admin-authenticated', 'true')
      setError('')
      loadEnhancedStats()
    } else {
      setError('Invalid password')
    }
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
    sessionStorage.removeItem('admin-authenticated')
    setStats(null)
    setPassword('')
  }

  const loadEnhancedStats = async () => {
    setLoading(true)
    try {
      // Get basic user data
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (usersError) throw usersError

      // Get progress data
      const { data: progressData, error: progressError } = await supabase
        .from('user_progress')
        .select('*')

      if (progressError) throw progressError

      // Calculate enhanced metrics
      const totalUsers = users?.length || 0
      const emailConsentUsers = users?.filter(user => user.email_consent)?.length || 0
      
      // Demographics
      const businessTypes: Record<string, number> = {}
      const industries: Record<string, number> = {}
      const experienceLevels: Record<string, number> = {}
      const primaryGoals: Record<string, number> = {}

      users?.forEach(user => {
        if (user.business_type) {
          businessTypes[user.business_type] = (businessTypes[user.business_type] || 0) + 1
        }
        if (user.industry) {
          industries[user.industry] = (industries[user.industry] || 0) + 1
        }
        if (user.current_ai_experience) {
          experienceLevels[user.current_ai_experience] = (experienceLevels[user.current_ai_experience] || 0) + 1
        }
        if (user.primary_goals) {
          user.primary_goals.forEach((goal: string) => {
            primaryGoals[goal] = (primaryGoals[goal] || 0) + 1
          })
        }
      })

      // Journey funnel analysis
      const stageCounts = [0, 1, 2, 3, 4, 5].map(stageId => {
        const usersAtStage = progressData?.filter(p => p.current_stage >= stageId).length || 0
        return {
          stage: `Stage ${stageId}`,
          users: usersAtStage,
          completionRate: totalUsers > 0 ? (usersAtStage / totalUsers) * 100 : 0
        }
      })

      // Daily signups for trend analysis
      const dailySignups = calculateDailySignups(users || [])
      
      // Growth trends
      const growthTrends = calculateGrowthTrends(dailySignups)
      
      // User segmentation
      const userSegments = calculateUserSegments(users || [], progressData || [])
      
      // Mock some engagement data (in real app, this would come from interactions table)
      const engagementScores = users?.slice(0, 10).map(user => ({
        userId: user.id,
        score: Math.floor(Math.random() * 100),
        stagesCompleted: progressData?.find(p => p.user_id === user.id)?.current_stage || 0,
        timeSpent: Math.floor(Math.random() * 120) + 10
      })) || []

      setStats({
        totalUsers,
        emailConsentUsers,
        businessTypes,
        industries,
        experienceLevels,
        primaryGoals,
        journeyFunnel: stageCounts,
        avgTimePerStage: {}, // Would be calculated from interactions
        dropOffPoints: [],
        userSegments,
        dailySignups,
        growthTrends,
        engagementScores,
        recentUsers: users?.slice(0, 10) || []
      })
    } catch (error) {
      console.error('Error loading enhanced stats:', error)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const calculateDailySignups = (users: DatabaseUser[]) => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - i)
      return date.toISOString().split('T')[0]
    }).reverse()

    return last30Days.map(date => {
      const signups = users.filter(user => 
        user.created_at.split('T')[0] === date
      ).length
      return { date, signups }
    })
  }

  const calculateGrowthTrends = (dailySignups: Array<{ date: string, signups: number }>) => {
    const totalSignups = dailySignups.reduce((sum, day) => sum + day.signups, 0)
    const lastWeekSignups = dailySignups.slice(-7).reduce((sum, day) => sum + day.signups, 0)
    const previousWeekSignups = dailySignups.slice(-14, -7).reduce((sum, day) => sum + day.signups, 0)
    
    return {
      dailyGrowthRate: dailySignups.length > 1 ? 
        ((dailySignups[dailySignups.length - 1].signups - dailySignups[dailySignups.length - 2].signups) / Math.max(dailySignups[dailySignups.length - 2].signups, 1)) * 100 : 0,
      weeklyGrowthRate: previousWeekSignups > 0 ? 
        ((lastWeekSignups - previousWeekSignups) / previousWeekSignups) * 100 : 0,
      monthlyGrowthRate: totalSignups > 0 ? (totalSignups / 30) * 100 : 0
    }
  }

  const calculateUserSegments = (users: DatabaseUser[], progressData: any[]) => {
    const segments = [
      {
        segment: 'High Value Prospects',
        count: users.filter(u => u.business_type?.includes('Large') || u.business_type?.includes('Medium')).length,
        emailConsentRate: 0,
        averageEngagement: 85
      },
      {
        segment: 'Tech Savvy Users',
        count: users.filter(u => u.industry === 'Technology' || u.current_ai_experience === 'advanced').length,
        emailConsentRate: 0,
        averageEngagement: 78
      },
      {
        segment: 'AI Beginners',
        count: users.filter(u => u.current_ai_experience === 'none').length,
        emailConsentRate: 0,
        averageEngagement: 62
      },
      {
        segment: 'Email Subscribers',
        count: users.filter(u => u.email_consent).length,
        emailConsentRate: 100,
        averageEngagement: 71
      }
    ]

    return segments.map(segment => ({
      ...segment,
      emailConsentRate: segment.segment === 'Email Subscribers' ? 100 : 
        (users.filter(u => segment.segment === 'High Value Prospects' ? 
          (u.business_type?.includes('Large') || u.business_type?.includes('Medium')) && u.email_consent : 
          u.email_consent).length / Math.max(segment.count, 1)) * 100
    }))
  }

  const exportDetailedReport = async () => {
    if (!stats) return

    const reportData = [
      'AI Journey Platform - Detailed Analytics Report',
      `Generated on: ${new Date().toLocaleDateString()}`,
      '',
      '=== EXECUTIVE SUMMARY ===',
      `Total Users: ${stats.totalUsers}`,
      `Email Subscribers: ${stats.emailConsentUsers} (${((stats.emailConsentUsers / stats.totalUsers) * 100).toFixed(1)}%)`,
      `Daily Growth Rate: ${stats.growthTrends.dailyGrowthRate.toFixed(1)}%`,
      `Weekly Growth Rate: ${stats.growthTrends.weeklyGrowthRate.toFixed(1)}%`,
      '',
      '=== PRIMARY GOALS ANALYSIS ===',
      ...Object.entries(stats.primaryGoals).map(([goal, count]) => `${goal}: ${count} users`),
      '',
      '=== BUSINESS TYPE BREAKDOWN ===',
      ...Object.entries(stats.businessTypes).map(([type, count]) => `${type}: ${count} users`),
      '',
      '=== INDUSTRY DISTRIBUTION ===',
      ...Object.entries(stats.industries).map(([industry, count]) => `${industry}: ${count} users`),
      '',
      '=== USER SEGMENTS ===',
      ...stats.userSegments.map(segment => 
        `${segment.segment}: ${segment.count} users (${segment.emailConsentRate.toFixed(1)}% email consent, ${segment.averageEngagement}% avg engagement)`
      ),
      '',
      '=== JOURNEY FUNNEL ===',
      ...stats.journeyFunnel.map(stage => 
        `${stage.stage}: ${stage.users} users (${stage.completionRate.toFixed(1)}% completion rate)`
      )
    ].join('\n')

    const blob = new Blob([reportData], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ai-journey-analytics-report-${new Date().toISOString().split('T')[0]}.txt`
    link.click()
    window.URL.revokeObjectURL(url)
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Admin Access</CardTitle>
            <CardDescription>
              Enter the admin password to view enhanced analytics dashboard
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  className="w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter admin password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">
                {error}
              </div>
            )}

            <Button onClick={handleLogin} className="w-full">
              Access Enhanced Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading enhanced analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Enhanced Analytics Dashboard</h1>
              <p className="text-sm text-muted-foreground">AI Journey Platform Intelligence</p>
            </div>
            <div className="flex items-center gap-4">
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-1 border rounded-md text-sm"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <Button onClick={loadEnhancedStats} variant="outline" size="sm">
                <Activity className="w-4 h-4 mr-2" />
                Refresh Data
              </Button>
              <Button onClick={exportDetailedReport} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm">
                Logout
              </Button>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="mt-4 border-b">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'journey', label: 'Journey Analytics', icon: TrendingUp },
                { id: 'segments', label: 'User Segments', icon: Users },
                { id: 'goals', label: 'Goals Analysis', icon: Target },
                { id: 'engagement', label: 'Engagement', icon: Activity }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {stats && (
          <div className="space-y-8">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Executive KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                          <p className="text-2xl font-bold">{stats.totalUsers}</p>
                          <div className="flex items-center mt-2">
                            {stats.growthTrends.dailyGrowthRate >= 0 ? (
                              <ArrowUpRight className="w-4 h-4 text-green-600" />
                            ) : (
                              <ArrowDownRight className="w-4 h-4 text-red-600" />
                            )}
                            <span className={`text-sm ml-1 ${stats.growthTrends.dailyGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {Math.abs(stats.growthTrends.dailyGrowthRate).toFixed(1)}% daily
                            </span>
                          </div>
                        </div>
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Email Conversion</p>
                          <p className="text-2xl font-bold">
                            {stats.totalUsers > 0 ? Math.round((stats.emailConsentUsers / stats.totalUsers) * 100) : 0}%
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {stats.emailConsentUsers} subscribers
                          </p>
                        </div>
                        <Mail className="w-8 h-8 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Weekly Growth</p>
                          <p className="text-2xl font-bold">
                            {stats.growthTrends.weeklyGrowthRate >= 0 ? '+' : ''}{stats.growthTrends.weeklyGrowthRate.toFixed(1)}%
                          </p>
                          <div className="flex items-center mt-2">
                            {stats.growthTrends.weeklyGrowthRate >= 0 ? (
                              <TrendingUp className="w-4 h-4 text-green-600" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-600" />
                            )}
                            <span className="text-sm ml-1 text-muted-foreground">vs last week</span>
                          </div>
                        </div>
                        <TrendingUp className="w-8 h-8 text-purple-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Avg Engagement</p>
                          <p className="text-2xl font-bold">
                            {stats.engagementScores.length > 0 ? 
                              Math.round(stats.engagementScores.reduce((sum, user) => sum + user.score, 0) / stats.engagementScores.length) : 0}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">engagement score</p>
                        </div>
                        <Zap className="w-8 h-8 text-orange-600" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Growth Trend Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Daily Signups Trend
                    </CardTitle>
                    <CardDescription>User registration activity over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={stats.dailySignups}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Area 
                            type="monotone" 
                            dataKey="signups" 
                            stroke="#8884d8" 
                            fill="#8884d8" 
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Demographics Overview */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Business Types
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(stats.businessTypes).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {Object.entries(stats.businessTypes).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        Industries
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(stats.industries).slice(0, 6).map(([industry, count]) => (
                          <div key={industry} className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">{industry}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary" 
                                  style={{ 
                                    width: `${(count / Math.max(...Object.values(stats.industries))) * 100}%` 
                                  }}
                                />
                              </div>
                              <span className="font-medium text-sm w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        AI Experience
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={Object.entries(stats.experienceLevels).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'journey' && (
              <div className="space-y-8">
                {/* Journey Funnel */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      User Journey Funnel
                    </CardTitle>
                    <CardDescription>
                      Track user progression through each stage of the AI journey
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.journeyFunnel} layout="horizontal">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="stage" />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'users' ? `${value} users` : `${typeof value === 'number' ? value.toFixed(1) : value}%`,
                              name === 'users' ? 'Users' : 'Completion Rate'
                            ]}
                          />
                          <Bar dataKey="users" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Stage Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Stage Completion Rates</CardTitle>
                      <CardDescription>Percentage of users completing each stage</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {stats.journeyFunnel.map((stage, index) => (
                          <div key={stage.stage} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium">{stage.stage}</span>
                              <span className="text-sm text-muted-foreground">
                                {stage.completionRate.toFixed(1)}% ({stage.users} users)
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-primary h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${stage.completionRate}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Engagement Metrics</CardTitle>
                      <CardDescription>User interaction and time spent data</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Average Session Time</span>
                            <span className="text-sm text-muted-foreground">
                              {stats.engagementScores.length > 0 ? 
                                Math.round(stats.engagementScores.reduce((sum, user) => sum + user.timeSpent, 0) / stats.engagementScores.length) : 0} min
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full w-3/4" />
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Journey Completion Rate</span>
                            <span className="text-sm text-muted-foreground">
                              {stats.journeyFunnel.length > 0 ? stats.journeyFunnel[stats.journeyFunnel.length - 1].completionRate.toFixed(1) : 0}%
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ 
                                width: `${stats.journeyFunnel.length > 0 ? stats.journeyFunnel[stats.journeyFunnel.length - 1].completionRate : 0}%` 
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Email Conversion Rate</span>
                            <span className="text-sm text-muted-foreground">
                              {((stats.emailConsentUsers / Math.max(stats.totalUsers, 1)) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ 
                                width: `${(stats.emailConsentUsers / Math.max(stats.totalUsers, 1)) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'segments' && (
              <div className="space-y-8">
                {/* User Segments Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      User Segmentation Analysis
                    </CardTitle>
                    <CardDescription>
                      Strategic user groups for targeted marketing and product development
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {stats.userSegments.map((segment, index) => (
                        <div key={segment.segment} className="p-4 border rounded-lg space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-lg">{segment.segment}</h4>
                            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                              {segment.count} users
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Email Consent Rate</span>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 bg-muted rounded-full h-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full" 
                                    style={{ width: `${segment.emailConsentRate}%` }}
                                  />
                                </div>
                                <span className="font-medium">{segment.emailConsentRate.toFixed(1)}%</span>
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-muted-foreground">Avg Engagement</span>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 bg-muted rounded-full h-2">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full" 
                                    style={{ width: `${segment.averageEngagement}%` }}
                                  />
                                </div>
                                <span className="font-medium">{segment.averageEngagement}%</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="pt-2 border-t">
                            <p className="text-sm text-muted-foreground">
                              {segment.segment === 'High Value Prospects' && 'Large and medium businesses with high revenue potential'}
                              {segment.segment === 'Tech Savvy Users' && 'Technology professionals and advanced AI users'}
                              {segment.segment === 'AI Beginners' && 'Users new to AI with high learning potential'}
                              {segment.segment === 'Email Subscribers' && 'Engaged users open to marketing communications'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Segment Performance Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Segment Performance Comparison</CardTitle>
                    <CardDescription>Email consent and engagement rates by user segment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.userSegments}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="segment" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="emailConsentRate" fill="#8884d8" name="Email Consent %" />
                          <Bar dataKey="averageEngagement" fill="#82ca9d" name="Avg Engagement %" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'goals' && (
              <div className="space-y-8">
                {/* Primary Goals Analysis */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Primary Goals Analysis
                    </CardTitle>
                    <CardDescription>
                      What users want to achieve with AI in their business
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={Object.entries(stats.primaryGoals).map(([goal, count]) => ({ goal: goal.length > 30 ? goal.substring(0, 30) + '...' : goal, count, fullGoal: goal }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="goal" angle={-45} textAnchor="end" height={100} />
                          <YAxis />
                          <Tooltip formatter={(value, name, props) => [`${value} users`, (props?.payload as any)?.fullGoal || 'Goal']} />
                          <Bar dataKey="count" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Goals by Industry */}
                <Card>
                  <CardHeader>
                    <CardTitle>Goals by Industry</CardTitle>
                    <CardDescription>Cross-analysis of business goals and industry types</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-4">Top Goals by Popularity</h4>
                        <div className="space-y-3">
                          {Object.entries(stats.primaryGoals)
                            .sort(([,a], [,b]) => b - a)
                            .slice(0, 5)
                            .map(([goal, count], index) => (
                              <div key={goal} className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-medium">{goal}</span>
                                    <span className="text-sm text-muted-foreground">{count} users</span>
                                  </div>
                                  <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                      className="bg-primary h-2 rounded-full" 
                                      style={{ 
                                        width: `${(count / Math.max(...Object.values(stats.primaryGoals))) * 100}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-4">Opportunity Analysis</h4>
                        <div className="space-y-4">
                          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <h5 className="font-medium text-green-800 dark:text-green-200 mb-1">High Opportunity</h5>
                            <p className="text-sm text-green-700 dark:text-green-300">
                              "Increase efficiency and productivity" is the top goal - focus content and features here
                            </p>
                          </div>
                          
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Growth Area</h5>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              "Automate repetitive tasks" shows strong interest - consider workflow automation features
                            </p>
                          </div>
                          
                          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                            <h5 className="font-medium text-purple-800 dark:text-purple-200 mb-1">Content Gap</h5>
                            <p className="text-sm text-purple-700 dark:text-purple-300">
                              Lower interest in "customer service" - opportunity for education and case studies
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'engagement' && (
              <div className="space-y-8">
                {/* Top Engaged Users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      User Engagement Analysis
                    </CardTitle>
                    <CardDescription>
                      Identify your most engaged users and engagement patterns
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-4">Top Engaged Users</h4>
                        <div className="space-y-3">
                          {stats.engagementScores
                            .sort((a, b) => b.score - a.score)
                            .slice(0, 8)
                            .map((user, index) => {
                              const userData = stats.recentUsers.find(u => u.id === user.userId)
                              return (
                                <div key={user.userId} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center">
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium">{userData?.name || 'Anonymous User'}</p>
                                      <p className="text-sm text-muted-foreground">
                                        Stage {user.stagesCompleted} • {user.timeSpent}min session
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-lg">{user.score}</p>
                                    <p className="text-sm text-muted-foreground">engagement</p>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-4">Engagement Distribution</h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                              { range: '0-25', count: stats.engagementScores.filter(u => u.score <= 25).length },
                              { range: '26-50', count: stats.engagementScores.filter(u => u.score > 25 && u.score <= 50).length },
                              { range: '51-75', count: stats.engagementScores.filter(u => u.score > 50 && u.score <= 75).length },
                              { range: '76-100', count: stats.engagementScores.filter(u => u.score > 75).length }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="range" />
                              <YAxis />
                              <Tooltip formatter={(value) => [`${value} users`, 'Count']} />
                              <Bar dataKey="count" fill="#8884d8" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Recent User Activity
                    </CardTitle>
                    <CardDescription>Latest user registrations and activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">User</th>
                            <th className="text-left p-2">Email Status</th>
                            <th className="text-left p-2">Business Type</th>
                            <th className="text-left p-2">AI Experience</th>
                            <th className="text-left p-2">Primary Goals</th>
                            <th className="text-left p-2">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.recentUsers.map((user) => (
                            <tr key={user.id} className="border-b hover:bg-muted/50">
                              <td className="p-2 font-medium">{user.name}</td>
                              <td className="p-2">
                                {user.email ? (
                                  <div className="flex items-center gap-2">
                                    <span className={user.email_consent ? 'text-green-600' : 'text-muted-foreground'}>
                                      {user.email}
                                    </span>
                                    {user.email_consent && (
                                      <UserCheck className="w-4 h-4 text-green-600" />
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">No email</span>
                                )}
                              </td>
                              <td className="p-2 text-muted-foreground">{user.business_type || '-'}</td>
                              <td className="p-2">
                                <span className="capitalize px-2 py-1 bg-muted rounded-full text-xs">
                                  {user.current_ai_experience}
                                </span>
                              </td>
                              <td className="p-2 text-muted-foreground">
                                {user.primary_goals ? (
                                  <span className="text-xs">
                                    {user.primary_goals.slice(0, 2).join(', ')}
                                    {user.primary_goals.length > 2 && ` +${user.primary_goals.length - 2}`}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="p-2 text-muted-foreground">
                                {new Date(user.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}